import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { useForm } from 'react-hook-form';
import { NotificationContext } from '../../contexts/NotificationContext';
import { useEnumState } from '../../hooks/useEnumState';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { TextInputController } from '../InputControllers/TextInputController';
import { useQueryParam } from '../../hooks/useQueryParam';
import { AuthGoBack } from '../AuthDialog/AuthGoBack';
import { GuestRoutes } from '../../routers/GuestRouter';
import { AuthDialog } from '../AuthDialog/AuthDialog';
import { Trans, useTranslation } from 'react-i18next';
import { apiAuth } from '../../resources/api/apiAuth';
import { useRouteWithQuery } from '../../hooks/useRouteWithQuery';
import { styled } from '@mui/styles';
import { useApiTranslation } from '../../hooks/useApiTranslation';

const { useRequestPasswordReset } = apiAuth;

const Code = styled('div')(({ theme }) => ({
  fontFamily: 'monospace',
  background: theme.palette.background.paper,
  padding: theme.spacing(1),
  margin: `${theme.spacing(1)} auto`,
  display: 'inline-block',
  color: theme.palette.text.disabled,
}));

const EmailAddress: FC = ({ children }) => (
  <div>
    <Code>{children}</Code>
  </div>
);

const Center = styled('div')(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

export const AuthRecoverAccount: FC = () => {
  const { addNotification } = useContext(NotificationContext);
  const [sentEmail, setSentEmail] = useState('');
  const apiErrorHandler = useApiErrorHandler();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();
  const email = useQueryParam('email');
  const { t } = useTranslation();
  const { apiT } = useApiTranslation();

  const { execute: recoverAccount } = useRequestPasswordReset();

  const [done, setDone] = useEnumState(false, true);

  const onSubmit = useCallback(async (data) => {
    try {
      setSentEmail(data.email);
      const result = await recoverAccount(data.email);
      addNotification({
        message: apiT(result.message) || t('auth.recovery.emailSentNotification'),
        severity: 'success',
      });
      setDone();
    } catch (e) {
      apiErrorHandler(e, t('auth.recovery.failure'));
    }
  }, []);

  useEffect(() => {
    reset({ email: email ?? '' });
  }, [email]);

  const signInUrl = useRouteWithQuery(GuestRoutes.SignIn, [], { email: sentEmail }, true);

  return (
    <AuthDialog
      title={t('auth.recovery.title')}
      subtitle={done ? void 0 : t('auth.recovery.encouragement')}
      onSubmit={handleSubmit(onSubmit)}
      actions={[
        <AuthGoBack key="goBack" to={signInUrl} />,
        done ? null : (
          <Button key="recover" variant="contained" color="primary" type="submit">
            {t('auth.recovery.submitButton')}
          </Button>
        ),
      ]}
    >
      {!done ? (
        <TextInputController
          control={control}
          errors={errors}
          label={t('auth.inputs.email.label')}
          name="email"
          inputType="email"
          rules={{ required: true }}
        />
      ) : (
        <Center>
          <Trans
            i18nKey={'auth.recovery.done'}
            components={{ EmailAddress: <EmailAddress />, LineBreak: <br /> }}
            values={{ email: sentEmail }}
          />
        </Center>
      )}
    </AuthDialog>
  );
};
